import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Delete, Block, CheckCircle, Info, Visibility } from "@mui/icons-material";
import Edit from "@mui/icons-material/Edit";
import {
  getUsers, suspendUser, activateUser, deleteUser, getUserDetails, updateUser
} from "../services/userService";
import FancyContextMenu, { ContextMenuAction } from "../components/FancyContextMenu";
import { StyledPaper } from "../assets/styles/tableStyle"
import { useConfirm } from "../components/feedback/ConfirmDialog";
import { useToast } from "../lib/useToast";
import { getApiErrorMessage } from "../lib/toast";
import { useCan } from "../auth/Can";
import { useDebounce } from "../hooks/useDebounce";
import { useCursorList, toCursorPage } from "../hooks/useCursorList";
import LoadMoreBar from "../components/common/LoadMoreBar";
import { User, booleanFields, editableFields } from "./users/userTypes";
import UserTable from "./users/UserTable";
import UserFiltersToolbar from "./users/UserFiltersToolbar";
import { useAppVersionOptions } from "../hooks/useAppVersionOptions";
import UserQuickViewModal from "./users/UserQuickViewModal";

export type { User };

const UserPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const { can } = useCan();
  const canWriteUsers = can('users:write');

  // `loading` is kept for mutation spinners; table loading comes from the hook.
  const [loading, setLoading] = useState(false);

  // Filters State
  const [searchTerm, setSearchTerm] = useState<string>(searchParams.get('search') || "");
  // Seeded from the URL: the App Adoption panel deep-links rider cohorts as ?role=rider&appVersion=…
  // The backend picks WHICH version column the appVersion filter targets from this role param
  // (rider → riderAppVersion, everything else → driverAppVersion), so a version deep-link without
  // its role would silently filter the wrong binary's column and show an empty/false cohort.
  const [roleFilter, setRoleFilter] = useState<string>(searchParams.get('role') || "all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<string>('');

  // all | unknown (old app) | eligible (>= live location_ping gate) | <exact versionCode>
  // Seeded from the URL so the App Adoption panel can deep-link straight into a cohort.
  const [appVersionFilter, setAppVersionFilter] = useState<string>(searchParams.get('appVersion') || "all");
  // The dropdown's build numbers must come from the SAME binary the backend will filter on. The
  // backend targets riderAppVersion only when role='rider' and the DRIVER column for every other
  // role value (including 'all'), so this mirrors that rule exactly — offering rider build numbers
  // while filtering the driver column would produce permanently-empty results. Per-role responses
  // are cached in the hook.
  const appVersionOptions = useAppVersionOptions(roleFilter === 'rider' ? 'rider' : 'driver');

  // Server-side, cursor-paginated, debounced search (scales to millions).
  const debouncedSearch = useDebounce(searchTerm);
  const usersList = useCursorList<User>({
    key: ['users', { search: debouncedSearch, role: roleFilter, status: statusFilter, appVersion: appVersionFilter }],
    pageSize: 25,
    fetchPage: async ({ cursor, limit }) => {
      const res = await getUsers({
        cursor,
        limit,
        search: debouncedSearch || undefined,
        role: roleFilter !== 'all' ? roleFilter : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        appVersion: appVersionFilter !== 'all' ? appVersionFilter : undefined,
      });
      return toCursorPage<User>(res.data);
    },
  });
  const refreshUsers = () => usersList.refetch();

  // Modal State
  const [detailOpen, setDetailOpen] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editData, setEditData] = useState<Partial<User>>({});
  const [editLoading, setEditLoading] = useState<boolean>(false);
  const [menu, setMenu] = useState<{ top: number; left: number } | null>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const handleContextMenu = (event: React.MouseEvent, row: any) => {
    event.preventDefault();
    setSelectedRow(row);
    setMenu({ top: event.clientY, left: event.clientX });
  };

  const handleCloseMenu = () => setMenu(null);

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const actions: ContextMenuAction[] = [
    {
      dividerBefore: true,
      label: "View Full Details",
      icon: <Visibility color="primary" fontSize="small" />,
      onClick: () => {
        if (selectedRow) navigate(`/user/${selectedRow.id}`);
      },
      color: "primary",
    },
    // Edit + status toggle require users:write
    ...(canWriteUsers ? ([
      {
        label: "Edit / Quick View",
        icon: <Edit color="info" fontSize="small" />,
        onClick: () => handleRowClick(selectedRow.id),
        color: "info",
      },
      {
        label: selectedRow?.suspended ? "Activate" : "Suspend",
        icon: selectedRow?.suspended ? <CheckCircle color="success" fontSize="small" /> : <Block color="error" fontSize="small" />,
        onClick: () => selectedRow?.suspended ? handleActivate(selectedRow.id) : handleSuspend(selectedRow.id),
        color: selectedRow?.suspended ? "success" : "warning",
      },
    ] as ContextMenuAction[]) : []),
    {
      label: "Show Rides",
      icon: <Info color="info" fontSize="small" />,
      onClick: () => {
        if (!selectedRow?.firebaseUid) return;
        window.location.href = `/rides?${selectedRow.role === 'driver' ? 'driverUid' : 'riderUid'}=${selectedRow.firebaseUid}`;
      },
      color: "info",
    },
    {
      label: "Show Wallet",
      icon: <Info color="info" fontSize="small" />,
      onClick: () => {
        if (!selectedRow?.firebaseUid) return;
        handleShowWalletDetails(selectedRow);
      },
      color: "info",
    },
    ...(canWriteUsers ? ([{
      dividerBefore: true,
      label: "Delete",
      icon: <Delete color="error" fontSize="small" />,
      onClick: () => handleDelete(selectedRow.id),
      color: "error",
    }] as ContextMenuAction[]) : []),
  ];

  // Keep the search term in the URL (without re-triggering fetches).
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (debouncedSearch) params.set('search', debouncedSearch);
    else params.delete('search');
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleRowClick = async (id: number) => {
    setSelectedId(id);
    setDetailOpen(true);
    setEditLoading(true);
    try {
      const res = await getUserDetails(id);
      setSelectedUser(res.data);
      setEditData(res.data);
    } finally {
      setEditLoading(false);
    }
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedId(null);
    setSelectedUser(null);
    setEditData({});
  };

  const handleFieldChange = (field: keyof User, value: string | boolean) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveEdit = async () => {
    if (!selectedId) return;
    setEditLoading(true);
    try {
      const updateFields: any = {};
      [...editableFields, ...booleanFields].forEach((field) => {
        if (editData[field] !== undefined) updateFields[field] = editData[field];
      });
      await updateUser(selectedId, updateFields);
      toast.success("User updated");
      refreshUsers();
      setDetailOpen(false);
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Failed to update user"));
    } finally {
      setEditLoading(false);
    }
  };

  const handleSuspend = async (id: number) => {
    const ok = await confirm({
      title: "Suspend user",
      message: "Suspend this user? They will lose access to the app until reactivated.",
      destructive: true,
      confirmText: "Suspend",
    });
    if (!ok) return;
    setLoading(true);
    try {
      await suspendUser(id);
      toast.success("User suspended");
      refreshUsers();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Failed to suspend user"));
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (id: number) => {
    setLoading(true);
    try {
      await activateUser(id);
      toast.success("User activated");
      refreshUsers();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Failed to activate user"));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({
      title: "Delete user",
      message: "This permanently deletes the user and cannot be undone. Continue?",
      destructive: true,
      confirmText: "Delete",
    });
    if (!ok) return;
    setLoading(true);
    try {
      await deleteUser(id);
      toast.success("User deleted");
      refreshUsers();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Failed to delete user"));
    } finally {
      setLoading(false);
    }
  };

  const handleShowWalletDetails = (row: any) => {
    if (!row?.firebaseUid) return;
    let query = "";
    if (row.role === "driver") {
      query = `?driverUid=${encodeURIComponent(row.firebaseUid)}`;
    } else {
      query = `?riderUid=${encodeURIComponent(row.firebaseUid)}`;
    }
    window.location.href = `/wallet-transactions${query}`;
  };

  // Search + role + status filtering is applied SERVER-SIDE. Here we only sort
  // the already-loaded rows (column headers act on the loaded page set).
  const displayRows = useMemo(() => {
    const rows = usersList.rows;
    if (!orderBy) return rows;
    return [...rows].sort((a, b) => {
      const valueA = a[orderBy as keyof User];
      const valueB = b[orderBy as keyof User];
      if (valueA === undefined || valueA === null) return order === 'asc' ? 1 : -1;
      if (valueB === undefined || valueB === null) return order === 'asc' ? -1 : 1;
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return order === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      }
      if (valueA < valueB) return order === 'asc' ? -1 : 1;
      if (valueA > valueB) return order === 'asc' ? 1 : -1;
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersList.rows, order, orderBy]);

  return (
      <StyledPaper sx={{ maxWidth: "98vw", width: "100%", margin: '0 auto', my: 2, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', overflow: 'hidden' }}>
        <UserFiltersToolbar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            roleFilter={roleFilter}
            setRoleFilter={setRoleFilter}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            appVersionFilter={appVersionFilter}
            setAppVersionFilter={setAppVersionFilter}
            appVersionOptions={appVersionOptions}
        />
        <UserTable
            rows={displayRows}
            loading={loading || usersList.isLoading}
            order={order}
            orderBy={orderBy}
            onSort={handleSort}
            onRowClick={handleRowClick}
            onContextMenu={handleContextMenu}
            appVersionGate={appVersionOptions.gate}
        />
        <LoadMoreBar
            count={displayRows.length}
            hasMore={usersList.hasMore}
            loading={usersList.isLoadingMore}
            onLoadMore={usersList.loadMore}
        />

        <UserQuickViewModal
            open={detailOpen}
            onClose={handleCloseDetail}
            editLoading={editLoading}
            selectedUser={selectedUser}
            editData={editData}
            onFieldChange={handleFieldChange}
            onSave={handleSaveEdit}
            canWrite={canWriteUsers}
            onGoToDetails={(id) => navigate(`/user/${id}`)}
        />
        <FancyContextMenu
            actions={actions}
            open={Boolean(menu)}
            onClose={handleCloseMenu}
            anchorPosition={menu}
            title={selectedRow?.name || selectedRow?.firebaseUid || "Actions"}
        />
      </StyledPaper>
  );
};

export default UserPage;
